import { ForwardedRef, forwardRef, useMemo, useState } from 'react';
import { Input, SearchSelectProps } from '~/components/Input';
import { BaseFormState } from '~/types/BaseFormState';
import { usePersonListQuery, UsePersonListQueryParams } from '../queries';
import { OptionTuple } from '~/components/Input/SearchSelect/SearchSelect';
import { PersonSerializerResponse } from '~/apis/ssf';

type Props<TFormState extends BaseFormState> = Omit<
  SearchSelectProps<TFormState>,
  'options' | 'search' | 'setSearch' | 'isLoading'
> & {
  query?: Omit<Exclude<UsePersonListQueryParams, undefined>, 'search' | 'page_size'>;
  disableWhenCannotRegister?: boolean;
};

function PersonSearchSelectOption(props: { results: PersonSerializerResponse[] | undefined; option: OptionTuple }) {
  const { option, results } = props;
  const [label, value] = option;

  return (
    <div>
      <div>{label}</div>
      <small style={{ display: 'block', lineHeight: 1, color: 'var(--color-text-lighter)' }}>
        {results?.find(r => r.id === value)?.license_id}
      </small>
    </div>
  );
}

function PersonSearchSelectElement<TFormState extends BaseFormState>(
  { disableWhenCannotRegister = false, ...props }: Props<TFormState>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const [search, setSearch] = useState<string>('');

  const { data, isLoading } = usePersonListQuery(
    {
      ...props.query,
      page_size: 100,
      search,
    },
    search.length > 3
  );

  const options = useMemo<[string, number, boolean][]>(() => {
    return (
      data?.results.map(({ id, first_name, last_name, club, can_register }) => {
        return [`${first_name} ${last_name} (${club.name})`, id, disableWhenCannotRegister ? !can_register : false];
      }) ?? []
    );
  }, [data, disableWhenCannotRegister]);

  return (
    <Input.SearchSelect
      renderOption={o => <PersonSearchSelectOption results={data?.results} option={o} />}
      setSearch={setSearch}
      isLoading={isLoading}
      options={options}
      search={search}
      ref={ref}
      {...props}
    />
  );
}

export const PersonSearchSelect = forwardRef(PersonSearchSelectElement) as <TFormState extends BaseFormState>(
  props: Props<TFormState> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => ReturnType<typeof PersonSearchSelectElement>;
